// What a bulk push, resolve, or acceptance of proposed dates (or keeping the current ones) on
// a full list of actions does to each ticked action, and
// which it leaves alone and why, before anything is sent. An action's own row asks the
// same questions (pushBlocker, needsOptionChoice), so the two agree on what may move.
import type {
  OperatorChange,
  OperatorChangeBody,
} from "../api/hooks/useActionChanges";
import type { DateMoveSubject, NextAction } from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "./attributeFormat";
import { needsOptionChoice, pushBlocker, pushTarget } from "./nextActions";

/** How a bulk push moves each action: by a number of days from its own due date, or all to
 * one date. */
export type BulkPush = { by: number } | { to: string };

export type BulkChoice =
  | { kind: "push"; push: BulkPush }
  | { kind: "resolve"; outcome: "done" | "dropped"; reason: string }
  /** Each action's open proposal to move its date, accepted. */
  | { kind: "accept_moves" }
  /** Each one declined: the current dates stay, as the single answer's Keep. */
  | { kind: "keep_moves" };

/** A ticked action, with what a change to it is quoted against. */
export interface BulkRow {
  action: NextAction;
  agent: string;
  agentLabel: string;
  projectId: string;
  projectName: string;
  /** The version of its specialist's lists the page shows. */
  version: number;
  /** Its project's today, which "by N days" counts from when it is overdue. */
  today: string;
  /** An agent's open proposal to move its date: answering it moves the date. */
  dateMove: DateMoveSubject | null;
}

export interface BulkPlanned {
  row: BulkRow;
  change: OperatorChange;
  /** What happens to it, as the confirmation lists it: "30 Oct to 6 Nov", "Done". */
  effect: string;
}

export interface BulkSkipped {
  row: BulkRow;
  why: string;
}

export interface BulkPlan {
  planned: BulkPlanned[];
  skipped: BulkSkipped[];
}

// The route takes at most this many changes to one specialist's lists in one write.
const MAX_CHANGES_PER_LISTS = 100;

const plan = (row: BulkRow, choice: BulkChoice): BulkPlanned | string => {
  if (choice.kind === "accept_moves") {
    if (!row.dateMove) return "no date move proposed";
    // The rules a push meets apply to the move it accepts; one refusal refuses the batch.
    const why = pushBlocker(row.action, false);
    if (why) return why;
    return {
      row,
      change: { kind: "accept_move", question_id: row.dateMove.question_id },
      effect: `${formatDay(row.dateMove.current)} to ${formatDay(row.dateMove.proposed)}`,
    };
  }
  if (choice.kind === "keep_moves") {
    if (!row.dateMove) return "no date move proposed";
    return {
      row,
      change: { kind: "keep_move", question_id: row.dateMove.question_id },
      effect: `Stays ${formatDay(row.dateMove.current)}, not ${formatDay(row.dateMove.proposed)}`,
    };
  }
  if (choice.kind === "push") {
    // The operator's date overrides an agent's open proposal: the push moves the date in
    // force, and the write closes the proposal it overtakes (Noaa, 28 Oct 2026).
    const why = pushBlocker(row.action, false);
    if (why) return why;
    const date =
      "by" in choice.push
        ? pushTarget(row.action.due ?? "", row.today, choice.push.by)
        : choice.push.to;
    if (date === row.action.due) return "is already due then";
    const moved = `${formatDay(row.action.due ?? "")} to ${formatDay(date)}`;
    return {
      row,
      change: { kind: "push", date },
      effect: !row.dateMove
        ? moved
        : date === row.dateMove.proposed
          ? `${moved}, as proposed`
          : `${moved}, instead of the proposed ${formatDay(row.dateMove.proposed)}`,
    };
  }
  if (row.action.closed) return "already resolved";
  if (needsOptionChoice(row.action, choice.outcome))
    return "a decision with options: resolve it from its own row to say which was taken";
  return {
    row,
    change: { kind: "resolve", outcome: choice.outcome, reason: choice.reason },
    effect:
      choice.outcome === "dropped"
        ? "No longer needed"
        : row.action.type === "decision"
          ? "Decided"
          : "Done",
  };
};

const listsKey = (row: BulkRow) => `${row.projectId}\u0000${row.agent}`;

export const planBulk = (rows: readonly BulkRow[], choice: BulkChoice): BulkPlan => {
  const planned: BulkPlanned[] = [];
  const skipped: BulkSkipped[] = [];
  const perLists = new Map<string, number>();
  for (const row of rows) {
    const result = plan(row, choice);
    if (typeof result === "string") {
      skipped.push({ row, why: result });
      continue;
    }
    const count = perLists.get(listsKey(row)) ?? 0;
    if (count >= MAX_CHANGES_PER_LISTS) {
      skipped.push({
        row,
        why: `over ${MAX_CHANGES_PER_LISTS} for one specialist at once: do the rest next`,
      });
      continue;
    }
    perLists.set(listsKey(row), count + 1);
    planned.push(result);
  }
  return { planned, skipped };
};

/** The planned changes as one request per specialist's lists on each project. None wakes
 * an agent: each takes its changes in on its next daily run, as a change from a row does
 * (Noaa, 29 Oct 2026). */
export const bulkBodies = (plan: BulkPlan, actorEmail: string): OperatorChangeBody[] => {
  const byLists = new Map<string, OperatorChangeBody>();
  for (const { row, change } of plan.planned) {
    const body = byLists.get(listsKey(row)) ?? {
      project_id: row.projectId,
      agent: row.agent,
      seen_version: row.version,
      changes: [],
      actor_email: actorEmail,
      wake: false,
    };
    body.changes.push({ item_id: row.action.action_id, change });
    byLists.set(listsKey(row), body);
  }
  return [...byLists.values()];
};
